import { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  Button,
  Space,
  Switch,
  message,
  Skeleton,
  Tooltip,
  Typography,
  Alert,
  Tag,
} from 'antd';
import {
  ToolOutlined,
  SaveOutlined,
  ExportOutlined,
  ThunderboltOutlined,
  RobotOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import {
  getSkillById,
  updateSkill,
  getAgentsForSkill,
  draftSkillWithAi,
  type AgentLinkRow,
  type SkillDraftResponse,
} from '@/api/skills';
import type { AiSkill } from '@/api/types';
import { colors } from '@/theme/tokens';
import { useStore } from '@/stores';
import 'highlight.js/styles/github.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// SkillEditorDrawer — AI-assisted authoring co-pilot
// ─────────────────────────────────────────────────────────────────────────────
//
// Designed to replace the workflow of "open Claude in another tab" for
// drafting skill content. Provides:
//
//   * Three-pane layout — Form / Live preview / Smart sidebar
//   * AI drafting — describe what you need, server prompts the LLM with
//     full skill catalog + agent context, returns a structured suggestion
//   * Similar-skills detection (client-side word-overlap on the cached
//     skill list) — surfaces duplicates before the user creates one
//   * Lint chips — naming convention, description token estimate, missing
//     "When to use / When NOT to use / Examples" sections in instructions
//   * Agent usage panel — every agent currently linked to this skill
//   * Live markdown preview — no tab switch, see what the agent's Skills
//     drawer will render side-by-side with the editor
//
// CONSTRAINTS
//   * Name read-only (it's the natural key — renaming is a coordinated
//     operation, separate flow).
//   * Version read-only — bumped automatically server-side on content
//     change.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  skillId: number | null;
  /** Optional row data so the title shows instantly while we re-fetch the body. */
  preview?: AiSkill | null;
  onClose: () => void;
  /** Called after a successful save so the list owner re-fetches. */
  onSaved?: () => void | Promise<void>;
  /** Optional. Scopes AI drafting to one agent's voice + skill set. */
  agentId?: number;
}

export function SkillEditorDrawer({ skillId, preview, onClose, onSaved, agentId }: Props) {
  const { themeStore, skillStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [form] = Form.useForm();
  const [skill, setSkill] = useState<AiSkill | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftInstructions, setDraftInstructions] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftName, setDraftName] = useState('');

  // Agents currently linked to this skill — fetched once when the drawer opens.
  const [linkedAgents, setLinkedAgents] = useState<AgentLinkRow[]>([]);

  // AI drafting state.
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<SkillDraftResponse | null>(null);

  useEffect(() => {
    if (skillId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAiSuggestion(null);
    void (async () => {
      try {
        const [full, agents] = await Promise.all([
          getSkillById(skillId),
          getAgentsForSkill(skillId).catch(() => [] as AgentLinkRow[]),
        ]);
        if (cancelled) return;
        setSkill(full);
        setLinkedAgents(agents);
        form.setFieldsValue({
          name: full.name,
          description: full.description ?? '',
          category: full.category ?? '',
          version: full.version ?? '',
          isActive: full.isActive,
          instructions: full.instructions ?? '',
        });
        setDraftInstructions(full.instructions ?? '');
        setDraftDescription(full.description ?? '');
        setDraftName(full.name ?? '');
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[SkillEditorDrawer] fetch failed:', err);
        setError(
          'We could not load this skill. Try again, or contact support if it keeps happening.',
        );
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skillId, form]);

  const headerSkill = skill ?? preview ?? null;

  // ── Smart computations ──────────────────────────────────────────────────

  // Similar-skills check — client-side word-overlap against the cached
  // catalog from the Skills list. Cheap and good enough for "is this
  // a duplicate" detection. A proper similarity service can replace this
  // later, but adding latency here would defeat the "feel intelligent"
  // promise.
  const similar = useMemo(
    () => findSimilarSkills(draftName, draftDescription, skillStore.skills, skill?.id ?? null),
    [draftName, draftDescription, skillStore.skills, skill?.id],
  );

  // Lint signals — naming convention, description length, instruction
  // section coverage. Each becomes a chip in the sidebar.
  const lints = useMemo(
    () => computeLints(draftName, draftDescription, draftInstructions),
    [draftName, draftDescription, draftInstructions],
  );

  // Token estimate for description (loaded every agent turn — keep tight).
  const descTokens = roughTokenCount(draftDescription);

  const handleSave = async () => {
    if (skill == null) return;
    let values: Record<string, unknown>;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const updated = await updateSkill({
        id: skill.id,
        name: skill.name, // read-only here
        description: (values.description as string) ?? null,
        category: (values.category as string) ?? null,
        instructions: (values.instructions as string) ?? null,
        isActive: values.isActive as boolean,
        version: skill.version,
      });
      setSkill(updated);
      void message.success('Skill saved');
      if (onSaved) await onSaved();
    } catch (err) {
      void message.error('Could not save: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setSaving(false);
    }
  };

  const askAi = async () => {
    if (!aiPrompt.trim()) return;
    setAiBusy(true);
    setAiSuggestion(null);
    try {
      const draft = await draftSkillWithAi({
        userRequest: aiPrompt.trim(),
        currentDraft: {
          name: draftName,
          description: draftDescription,
          category: form.getFieldValue('category'),
          instructions: draftInstructions,
        },
        agentId,
      });
      setAiSuggestion(draft);
    } catch (err) {
      void message.error((err as Error).message);
    } finally {
      setAiBusy(false);
    }
  };

  const applyAiSuggestion = () => {
    if (!aiSuggestion) return;
    const patch: Record<string, unknown> = {};
    if (aiSuggestion.description) patch.description = aiSuggestion.description;
    if (aiSuggestion.category) patch.category = aiSuggestion.category;
    if (aiSuggestion.instructions) patch.instructions = aiSuggestion.instructions;
    // Name is read-only on existing skills — only apply name suggestion
    // if we're authoring a new draft (not the case in this drawer today,
    // but it's the right shape for when we add Create-skill flow).
    form.setFieldsValue(patch);
    if (typeof patch.description === 'string') setDraftDescription(patch.description as string);
    if (typeof patch.instructions === 'string') setDraftInstructions(patch.instructions as string);
    void message.success('AI suggestion applied to the form. Review and save.');
  };

  return (
    <Drawer
      open={skillId != null}
      onClose={onClose}
      width="92%"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <ToolOutlined style={{ color: colors.violet[600], fontSize: 18 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {headerSkill?.name ?? 'Skill'}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Edit skill content
            </Text>
          </div>
        </div>
      }
      extra={
        <Space size="small">
          {skillId != null && (
            <Link to={`/skills/${skillId}`} target="_blank" rel="noopener noreferrer">
              <Tooltip title="Open the skill detail page in a new tab">
                <Button size="small" icon={<ExportOutlined />}>
                  Detail page
                </Button>
              </Tooltip>
            </Link>
          )}
          <Button
            size="small"
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={loading || skill == null}
            onClick={handleSave}
          >
            Save
          </Button>
        </Space>
      }
      styles={{ body: { padding: 0, background: isDark ? colors.slate[900] : '#FFFFFF' } }}
      destroyOnHidden
    >
      {loading && !skill ? (
        <div style={{ padding: 24 }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : error ? (
        <div style={{ padding: 24 }}>
          <Alert type="error" showIcon message={error} />
        </div>
      ) : skill ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 320px',
            gap: 16,
            padding: '20px 24px 80px',
          }}
        >
          {/* ── Pane 1: form ─────────────────────────────────────────── */}
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changed) => {
              if (typeof changed.instructions === 'string') setDraftInstructions(changed.instructions);
              if (typeof changed.description === 'string') setDraftDescription(changed.description);
              if (typeof changed.name === 'string') setDraftName(changed.name);
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: 12 }}>
              <Form.Item name="name" label="Name" tooltip="The natural key. Read-only here.">
                <Input disabled />
              </Form.Item>
              <Form.Item
                name="category"
                label="Category"
                tooltip="Free-string column. Use the Lookups page to rename across rows."
              >
                <Input placeholder="Clinical / Core / …" />
              </Form.Item>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
            <Form.Item
              name="description"
              label={
                <Space size={6}>
                  <span>Description</span>
                  <Text type="secondary" style={{ fontSize: 11.5 }}>
                    ≈{descTokens} tokens · loaded every agent turn
                  </Text>
                </Space>
              }
              tooltip="One sentence. Sits in the system prompt — keep it tight."
              rules={[{ required: true, message: 'Description is required' }]}
            >
              <Input.TextArea rows={3} maxLength={400} showCount />
            </Form.Item>
            <Form.Item name="version" label="Version">
              <Input disabled style={{ width: 140 }} />
            </Form.Item>
            <Form.Item
              name="instructions"
              label="Instructions (markdown)"
              tooltip="Full body. Loaded only when the skill is invoked. Supports GFM, code blocks, embedded HTML."
            >
              <Input.TextArea
                rows={20}
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, lineHeight: 1.6 }}
              />
            </Form.Item>
          </Form>

          {/* ── Pane 2: live preview ─────────────────────────────────── */}
          <div
            style={{
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              borderRadius: 10,
              background: isDark ? colors.slate[900] : '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: isDark ? colors.slate[400] : colors.slate[500],
                padding: '10px 16px',
                borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              }}
            >
              Live preview
            </div>
            <div
              style={{
                padding: '16px 20px',
                overflow: 'auto',
                color: isDark ? colors.slate[100] : colors.slate[800],
                fontSize: 14,
                lineHeight: 1.7,
                flex: 1,
              }}
            >
              {draftInstructions.trim().length === 0 ? (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Type some markdown in the editor — the rendered version will show here.
                </Text>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeHighlight]}
                >
                  {draftInstructions}
                </ReactMarkdown>
              )}
            </div>
          </div>

          {/* ── Pane 3: smart sidebar ────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
            <SmartCard title="✨ AI co-pilot" isDark={isDark} accent={colors.violet[600]}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Describe what you want — the model drafts a name, description, category, and full
                markdown body. Existing skills + agent context are passed in so it doesn't
                duplicate.
              </Text>
              <Input.TextArea
                rows={3}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={
                  agentId
                    ? 'Add a section explaining when to escalate to a human pharmacist…'
                    : 'A skill that helps a diabetic patient look up carbs in foods…'
                }
              />
              <Button
                type="primary"
                size="small"
                icon={<ThunderboltOutlined />}
                loading={aiBusy}
                disabled={!aiPrompt.trim()}
                onClick={askAi}
                style={{ marginTop: 8, width: '100%' }}
              >
                Draft with AI
              </Button>
              {aiSuggestion && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 10,
                    background: isDark ? `${colors.violet[700]}22` : colors.violet[50],
                    border: `1px solid ${isDark ? colors.violet[700] : colors.violet[200]}`,
                    borderRadius: 8,
                    fontSize: 12.5,
                    color: isDark ? colors.slate[200] : colors.slate[700],
                  }}
                >
                  {aiSuggestion.rationale && (
                    <div style={{ marginBottom: 6 }}>
                      <InfoCircleOutlined style={{ color: colors.violet[600], marginRight: 6 }} />
                      <b>Why:</b> {aiSuggestion.rationale}
                    </div>
                  )}
                  {aiSuggestion.similarSkillNames && aiSuggestion.similarSkillNames.length > 0 && (
                    <div style={{ marginBottom: 6 }}>
                      <WarningOutlined style={{ color: colors.amber[600], marginRight: 6 }} />
                      <b>Possible duplicates:</b>{' '}
                      {aiSuggestion.similarSkillNames.map((n) => (
                        <Tag key={n} color="orange" style={{ marginInline: 2 }}>
                          {n}
                        </Tag>
                      ))}
                    </div>
                  )}
                  <Button
                    size="small"
                    type="primary"
                    onClick={applyAiSuggestion}
                    style={{ width: '100%', marginTop: 4 }}
                  >
                    Apply to form
                  </Button>
                </div>
              )}
            </SmartCard>

            <SmartCard title="Linked agents" isDark={isDark} accent={colors.ink[600]}>
              {linkedAgents.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12.5 }}>
                  Not linked to any agent yet. Editing affects nothing live.
                </Text>
              ) : (
                <>
                  <Text style={{ fontSize: 12.5, marginBottom: 8, display: 'block' }}>
                    Editing this skill affects these <b>{linkedAgents.length}</b> agent
                    {linkedAgents.length === 1 ? '' : 's'}:
                  </Text>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {linkedAgents.map((a) => (
                      <Link
                        key={a.id}
                        to={`/agents/${a.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '5px 8px',
                          borderRadius: 6,
                          background: isDark ? colors.slate[800] : colors.slate[50],
                          color: isDark ? colors.slate[100] : colors.slate[700],
                          fontSize: 12.5,
                          textDecoration: 'none',
                        }}
                      >
                        <RobotOutlined style={{ color: colors.ink[600] }} />
                        {a.displayName ?? a.canonicalName ?? `Agent #${a.id}`}
                      </Link>
                    ))}
                  </Space>
                </>
              )}
            </SmartCard>

            <SmartCard
              title={`Lint (${lints.length === 0 ? 'all good' : `${lints.length} note${lints.length === 1 ? '' : 's'}`})`}
              isDark={isDark}
              accent={lints.length === 0 ? colors.green[600] : colors.amber[600]}
            >
              {lints.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                  <CheckCircleOutlined style={{ color: colors.green[600] }} />
                  <Text type="secondary">No issues detected.</Text>
                </div>
              ) : (
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  {lints.map((l, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'flex-start',
                        fontSize: 12.5,
                        color: l.severity === 'warn' ? colors.amber[700] : colors.slate[600],
                      }}
                    >
                      <WarningOutlined
                        style={{
                          color: l.severity === 'warn' ? colors.amber[600] : colors.slate[500],
                          marginTop: 2,
                          flexShrink: 0,
                        }}
                      />
                      <span>{l.message}</span>
                    </div>
                  ))}
                </Space>
              )}
            </SmartCard>

            <SmartCard
              title={`Similar skills (${similar.length})`}
              isDark={isDark}
              accent={similar.length === 0 ? colors.green[600] : colors.amber[600]}
            >
              {similar.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12.5 }}>
                  No close matches in the catalog. Looks original.
                </Text>
              ) : (
                <>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                    Top matches by name + description overlap. Editing one of these may be better
                    than creating a new one.
                  </Text>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {similar.map((s) => (
                      <div
                        key={s.id}
                        style={{
                          padding: '6px 10px',
                          background: isDark ? colors.slate[800] : colors.slate[50],
                          borderRadius: 6,
                          fontSize: 12.5,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                            {s.name}
                          </span>
                          <Tag color={s.score >= 0.7 ? 'red' : s.score >= 0.4 ? 'orange' : 'default'}>
                            {Math.round(s.score * 100)}%
                          </Tag>
                        </div>
                        <Text type="secondary" style={{ fontSize: 11.5, display: 'block', marginTop: 2 }}>
                          {s.description}
                        </Text>
                      </div>
                    ))}
                  </Space>
                </>
              )}
            </SmartCard>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}

// ── Smart sub-components ───────────────────────────────────────────────────

function SmartCard({
  title,
  accent,
  isDark,
  children,
}: {
  title: string;
  accent: string;
  isDark: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[300] : colors.slate[600],
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

// ── Smart heuristics ───────────────────────────────────────────────────────

interface LintNote {
  severity: 'warn' | 'info';
  message: string;
}

function computeLints(name: string, description: string, instructions: string): LintNote[] {
  const lints: LintNote[] = [];

  // Naming convention.
  if (name && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(name)) {
    lints.push({
      severity: 'warn',
      message: 'Name should be lowercase-hyphenated (e.g. "nutrition-food-nutrient-lookup").',
    });
  }

  // Description length budget.
  if (description) {
    const tokens = roughTokenCount(description);
    if (tokens > 80) {
      lints.push({
        severity: 'warn',
        message: `Description is ≈${tokens} tokens. Aim for under 60 — it loads on every agent turn.`,
      });
    }
  }

  // Required instruction sections.
  if (instructions && instructions.trim().length > 100) {
    const text = instructions.toLowerCase();
    if (!text.includes('when to use')) {
      lints.push({ severity: 'info', message: 'Consider adding a "When to use" section.' });
    }
    if (!text.includes('when not to use')) {
      lints.push({
        severity: 'info',
        message: 'Consider adding a "When NOT to use" section to prevent over-invocation.',
      });
    }
    if (!text.includes('example')) {
      lints.push({
        severity: 'info',
        message: 'Consider adding 2–4 realistic example user phrasings.',
      });
    }
  }

  return lints;
}

function roughTokenCount(text: string): number {
  // Cheap and conventional: ~4 chars per token. Good enough for an
  // editor-side budget hint.
  return Math.ceil((text ?? '').length / 4);
}

interface SimilarMatch {
  id: number;
  name: string;
  description: string;
  score: number; // 0..1
}

function findSimilarSkills(
  draftName: string,
  draftDescription: string,
  catalog: AiSkill[],
  excludeId: number | null,
): SimilarMatch[] {
  const tokensA = tokenize(`${draftName} ${draftDescription}`);
  if (tokensA.size === 0) return [];

  const matches: SimilarMatch[] = [];
  for (const s of catalog) {
    if (excludeId != null && s.id === excludeId) continue;
    const tokensB = tokenize(`${s.name} ${s.description ?? ''}`);
    if (tokensB.size === 0) continue;
    const score = jaccard(tokensA, tokensB);
    if (score < 0.18) continue;
    matches.push({ id: s.id, name: s.name, description: s.description ?? '', score });
  }
  matches.sort((a, b) => b.score - a.score);
  return matches.slice(0, 5);
}

function tokenize(s: string): Set<string> {
  return new Set(
    (s ?? '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length >= 3 && !STOP_WORDS.has(t)),
  );
}

function jaccard(a: Set<string>, b: Set<string>): number {
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  const union = a.size + b.size - inter;
  return union === 0 ? 0 : inter / union;
}

const STOP_WORDS = new Set([
  'the', 'and', 'for', 'with', 'this', 'that', 'when', 'use', 'used',
  'agent', 'patient', 'skill', 'should', 'must', 'has', 'have', 'are',
  'about', 'into', 'from', 'their', 'they', 'will', 'such', 'lookup',
]);
